import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase, apiFetch } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  Plus, ClipboardList, Trash2, Edit, Copy, Eye, GripVertical, X as XIcon,
  Link2, ExternalLink, Wand2, Search, Layers, Download,
} from "lucide-react";
import { StatusBadge, PageHeader, EmptyState, StatusTabs, formatDate } from "@/lib/ui";
import { supabase as _sb } from "@/lib/supabase";
import { fetchAndSave } from "@/lib/downloads";

const FIELD_TYPES = [
  { value: "text", label: "Short text" },
  { value: "textarea", label: "Long text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "select", label: "Dropdown" },
  { value: "radio", label: "Radio" },
  { value: "checkbox", label: "Checkbox" },
];

const CLIENT_MAP_OPTIONS = [
  { value: "_none", label: "— None —" },
  { value: "client.first_name", label: "Client · First name" },
  { value: "client.last_name", label: "Client · Last name" },
  { value: "client.preferred_name", label: "Client · Preferred name" },
  { value: "client.date_of_birth", label: "Client · Date of birth" },
  { value: "client.phone", label: "Client · Phone" },
  { value: "client.email", label: "Client · Email" },
  { value: "client.street_address", label: "Client · Street address" },
  { value: "client.city", label: "Client · City" },
  { value: "client.state", label: "Client · State" },
  { value: "client.zip_code", label: "Client · ZIP" },
  { value: "client.emergency_contact_name", label: "Client · Emergency contact" },
  { value: "client.emergency_contact_phone", label: "Client · Emergency phone" },
  { value: "client.insurance_info", label: "Client · Insurance info" },
  { value: "client.referral_source", label: "Client · Referral source" },
  { value: "client.notes", label: "Client · Notes" },
];

const BASIC_INTAKE_TEMPLATE = {
  name: "Basic Client Intake",
  description: "Standard intake form covering client, emergency contact, and service info. Responses auto-populate the client record.",
  is_intake: true,
  is_public: true,
  status: "active",
  submit_button_label: "Submit Intake",
  success_message: "Thank you — your intake has been received. We'll be in touch shortly.",
  fields: [
    { id: crypto.randomUUID(), type: "text", label: "First name", required: true, map_to: "client.first_name" },
    { id: crypto.randomUUID(), type: "text", label: "Last name", required: true, map_to: "client.last_name" },
    { id: crypto.randomUUID(), type: "text", label: "Preferred name", required: false, map_to: "client.preferred_name" },
    { id: crypto.randomUUID(), type: "date", label: "Date of birth", required: false, map_to: "client.date_of_birth" },
    { id: crypto.randomUUID(), type: "phone", label: "Phone", required: true, map_to: "client.phone" },
    { id: crypto.randomUUID(), type: "email", label: "Email", required: true, map_to: "client.email" },
    { id: crypto.randomUUID(), type: "text", label: "Street address", required: false, map_to: "client.street_address" },
    { id: crypto.randomUUID(), type: "text", label: "City", required: false, map_to: "client.city" },
    { id: crypto.randomUUID(), type: "text", label: "State", required: false, map_to: "client.state" },
    { id: crypto.randomUUID(), type: "text", label: "ZIP code", required: false, map_to: "client.zip_code" },
    { id: crypto.randomUUID(), type: "select", label: "Preferred contact method", required: false, options: ["Phone", "Email", "Text", "Any"] },
    { id: crypto.randomUUID(), type: "text", label: "Emergency contact name", required: false, map_to: "client.emergency_contact_name" },
    { id: crypto.randomUUID(), type: "text", label: "Emergency contact relationship", required: false },
    { id: crypto.randomUUID(), type: "phone", label: "Emergency contact phone", required: false, map_to: "client.emergency_contact_phone" },
    { id: crypto.randomUUID(), type: "email", label: "Emergency contact email", required: false },
    { id: crypto.randomUUID(), type: "text", label: "Service / program requested", required: false },
    { id: crypto.randomUUID(), type: "text", label: "Referral source", required: false, map_to: "client.referral_source" },
    { id: crypto.randomUUID(), type: "date", label: "Preferred start date", required: false, map_to: "client.intake_date" },
    { id: crypto.randomUUID(), type: "text", label: "Assigned staff", required: false, placeholder: "Name of staff to be assigned (link them via Care Team after)" },
    { id: crypto.randomUUID(), type: "textarea", label: "Additional notes", required: false, map_to: "client.notes" },
  ],
};

const STATUS_TABS = [
  { id: "all",      label: "All" },
  { id: "draft",    label: "Draft" },
  { id: "active",   label: "Active" },
  { id: "archived", label: "Archived" },
];

export default function Forms() {
  const { currentOrgId, currentRole } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [statusFilter, setStatusFilter] = useState(params.get("status") || "all");

  const [builderOpen, setBuilderOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [sharing, setSharing] = useState(null);
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const [detailForm, setDetailForm] = useState(null);   // ← drawer state

  const isReadOnly = currentRole === "read_only";
  const isAdmin = currentRole === "org_admin";

  // Sync filters to URL — Back-nav restores exact view.
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (q) next.set("q", q); else next.delete("q");
    if (statusFilter && statusFilter !== "all") next.set("status", statusFilter); else next.delete("status");
    setParams(next, { replace: true });
    /* eslint-disable-next-line */
  }, [q, statusFilter]);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase.from("form_templates")
      .select("*, form_responses(count)")
      .eq("organization_id", currentOrgId)
      .order("created_at", { ascending: false });
    const rows = data || [];
    setForms(rows);
    // Refresh open drawer with fresh data if the record still exists.
    if (detailForm) {
      const fresh = rows.find((f) => f.id === detailForm.id);
      if (fresh) setDetailForm(fresh);
    }
    setLoading(false);
  };

  useEffect(() => { if (currentOrgId) load(); /* eslint-disable-next-line */ }, [currentOrgId]);

  // Deep-link: ?id=xxx opens the corresponding drawer once forms load.
  useEffect(() => {
    const id = params.get("id");
    if (!id || !forms.length || detailForm) return;
    const f = forms.find((x) => x.id === id);
    if (f) { setDetailForm(f); params.delete("id"); setParams(params, { replace: true }); }
    /* eslint-disable-next-line */
  }, [forms, params.get("id")]);

  const openNew = () => { setEditing(null); setBuilderOpen(true); };
  const openEditFromDrawer = (f) => { setEditing(f); setBuilderOpen(true); };

  const remove = async (f) => {
    const rcount = f.form_responses?.[0]?.count || 0;
    if (!isAdmin) return toast.error("Only org admins can delete forms.");
    const warn = rcount > 0
      ? `Delete template "${f.name}"?\n\n${rcount} submitted response${rcount === 1 ? "" : "s"} will ALSO be permanently deleted (audit trail lost). Type-approve by clicking OK.`
      : `Delete template "${f.name}"? This cannot be undone.`;
    if (!window.confirm(warn)) return;
    try {
      await apiFetch(`/forms/${f.id}`, { method: "DELETE" });
      toast.success("Form template deleted");
      setDetailForm(null); setBuilderOpen(false);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const duplicate = async (f) => {
    try {
      const created = await apiFetch("/forms", { method: "POST",
        body: JSON.stringify({
          organization_id: currentOrgId, name: `${f.name} (copy)`, description: f.description,
          fields: f.fields, is_intake: f.is_intake, is_public: false, status: "draft",
          submit_button_label: f.submit_button_label, success_message: f.success_message,
        }) });
      toast.success("Duplicated");
      load();
      if (created?.id) setTimeout(() => setDetailForm(created), 300);
    } catch (e) { toast.error(e.message); }
  };

  const openShare = (f) => { setSharing(f); setShareOpen(true); };

  const createBasicTemplate = async () => {
    setCreatingTemplate(true);
    try {
      await apiFetch("/forms", { method: "POST",
        body: JSON.stringify({ ...BASIC_INTAKE_TEMPLATE, organization_id: currentOrgId }) });
      toast.success("Basic Client Intake form created");
      load();
    } catch (e) { toast.error(e.message); }
    finally { setCreatingTemplate(false); }
  };

  const hasBasicIntake = forms.some((f) => f.name === BASIC_INTAKE_TEMPLATE.name);

  const filtered = useMemo(() => forms.filter((f) => {
    if (statusFilter !== "all" && f.status !== statusFilter) return false;
    if (q && !`${f.name} ${f.description || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [forms, statusFilter, q]);

  const statusCounts = useMemo(() => {
    const c = { all: forms.length };
    forms.forEach((f) => { c[f.status] = (c[f.status] || 0) + 1; });
    return c;
  }, [forms]);

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="forms-page">
      <PageHeader
        title="Forms"
        subtitle="Templates you build once, then reuse — share by link, embed in intake, or fill in-app."
        actions={!isReadOnly && (
          <div className="flex gap-2">
            {!hasBasicIntake && (
              <Button data-testid="use-basic-template-btn" onClick={createBasicTemplate} disabled={creatingTemplate} variant="outline" className="rounded-full">
                <Wand2 size={14} className="mr-1.5" /> {creatingTemplate ? "Creating…" : "Load starter template"}
              </Button>
            )}
            <Button data-testid="new-form-btn" onClick={openNew} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">
              <Plus size={14} className="mr-1.5" /> New template
            </Button>
          </div>
        )}
      />

      {forms.length > 0 && (
        <StatusTabs
          tabs={STATUS_TABS}
          value={statusFilter}
          onChange={setStatusFilter}
          counts={statusCounts}
          testidPrefix="ffilter"
          right={
            <div className="relative hidden md:block">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
              <input
                data-testid="forms-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name or description…"
                className="bg-transparent border-0 outline-none text-sm pl-9 pr-3 min-w-[240px]"
              />
            </div>
          }
        />
      )}

      {loading ? (
        <div className="text-center py-16 text-[#57534E]">Loading forms…</div>
      ) : forms.length === 0 ? (
        <EmptyState icon={ClipboardList} title="No form templates yet"
          description="Create your first custom form template. Submitted responses show up under each template."
          action={!isReadOnly && <Button data-testid="empty-new-form-btn" onClick={openNew} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]"><Plus size={14} className="mr-1.5" /> New template</Button>} />
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-[#57534E] bg-white rounded-2xl border border-[#E8E2D9]">No form templates match your filters.</div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#E8E2D9] overflow-hidden">
          {/* Desktop table */}
          <table className="w-full text-sm hidden md:table">
            <thead className="bg-[#FAF8F5] text-xs uppercase tracking-widest text-[#57534E]">
              <tr>
                <th className="text-left px-4 py-3">Template name</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Fields</th>
                <th className="text-left px-4 py-3">Responses</th>
                <th className="text-left px-4 py-3">Updated</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8E2D9]">
              {filtered.map((f) => (
                <tr key={f.id} data-testid={`form-row-${f.id}`} onClick={() => setDetailForm(f)} className="hover:bg-[#FAF8F5] cursor-pointer">
                  <td className="px-4 py-3">
                    <div className="font-medium truncate">{f.name}</div>
                    {f.description && <div className="text-xs text-[#A8A29E] mt-0.5 truncate max-w-md">{f.description}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div className="flex gap-1 flex-wrap">
                      <span className="bg-[#FAF8F5] border border-[#E8E2D9] text-[#57534E] px-2 py-0.5 rounded-full">Template</span>
                      {f.is_intake && <span className="bg-[#FDE9D8] text-[#B85C1F] px-2 py-0.5 rounded-full">Intake</span>}
                      {f.is_public && <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">Public</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[#57534E]">{(f.fields || []).length}</td>
                  <td className="px-4 py-3 text-[#57534E]">{f.form_responses?.[0]?.count || 0}</td>
                  <td className="px-4 py-3 text-[#57534E]">{formatDate(f.updated_at)}</td>
                  <td className="px-4 py-3"><StatusBadge value={f.status} /></td>
                  <td className="px-4 py-3 text-right">
                    <button
                      data-testid={`form-view-${f.id}`}
                      onClick={(e) => { e.stopPropagation(); setDetailForm(f); }}
                      className="text-xs px-2 py-1 rounded-full text-[#57534E] hover:bg-[#FAF8F5] inline-flex items-center gap-1"
                    >
                      <Eye size={12} /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-[#E8E2D9]">
            {filtered.map((f) => (
              <button
                key={f.id}
                onClick={() => setDetailForm(f)}
                className="w-full text-left px-4 py-4 hover:bg-[#FAF8F5] active:bg-[#FDE9D8]/40 transition-colors"
                data-testid={`form-card-${f.id}`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-semibold min-w-0 truncate">{f.name}</div>
                  <StatusBadge value={f.status} />
                </div>
                <div className="text-xs text-[#A8A29E] mt-1 flex flex-wrap gap-x-2">
                  <span>{(f.fields || []).length} field{(f.fields || []).length === 1 ? "" : "s"}</span>
                  <span>· {f.form_responses?.[0]?.count || 0} response{(f.form_responses?.[0]?.count || 0) === 1 ? "" : "s"}</span>
                  <span>· Updated {formatDate(f.updated_at)}</span>
                </div>
                <div className="flex gap-1 mt-1.5 flex-wrap">
                  <span className="text-[10px] bg-[#FAF8F5] border border-[#E8E2D9] text-[#57534E] px-2 py-0.5 rounded-full">Template</span>
                  {f.is_intake && <span className="text-[10px] bg-[#FDE9D8] text-[#B85C1F] px-2 py-0.5 rounded-full">Intake</span>}
                  {f.is_public && <span className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">Public</span>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ─── Form Template Detail Drawer ────────────────────────────── */}
      <Sheet open={!!detailForm} onOpenChange={(v) => { if (!v) setDetailForm(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto bg-white" data-testid="form-detail-drawer">
          {detailForm && (() => {
            const f = detailForm;
            const responseCount = f.form_responses?.[0]?.count || 0;
            const canShare = f.is_public && f.status === "active";
            return (
              <>
                <SheetHeader className="pr-8">
                  <div className="text-[10px] uppercase tracking-widest text-[#A8A29E]" data-testid="form-drawer-record-type">Form Template</div>
                  <SheetTitle className="font-display tracking-tight text-2xl leading-tight" data-testid="form-drawer-title">{f.name}</SheetTitle>
                  <SheetDescription asChild>
                    <div className="flex gap-2 flex-wrap items-center">
                      <StatusBadge value={f.status} testid="form-drawer-status" />
                      {f.is_intake && <span className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#FDE9D8] text-[#B85C1F]">Intake</span>}
                      {f.is_public && <span className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">Public</span>}
                    </div>
                  </SheetDescription>
                </SheetHeader>

                <div className="mt-6 space-y-5">
                  {f.description && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#A8A29E] mb-1.5">Description</div>
                      <div className="text-sm whitespace-pre-wrap" data-testid="form-drawer-description">{f.description}</div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4 pt-3 border-t border-[#E8E2D9]">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#A8A29E] mb-1">Fields</div>
                      <div className="text-sm" data-testid="form-drawer-field-count">{(f.fields || []).length}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#A8A29E] mb-1">Submitted responses</div>
                      <button
                        data-testid="form-drawer-response-count"
                        onClick={() => { navigate(`/app/forms/${f.id}/responses`); setDetailForm(null); }}
                        className="text-sm text-[#B85C1F] hover:underline text-left"
                        disabled={responseCount === 0}
                      >
                        {responseCount} · {responseCount === 0 ? "none yet" : "view"}
                      </button>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#A8A29E] mb-1">Created</div>
                      <div className="text-sm text-[#57534E]" data-testid="form-drawer-created">{formatDate(f.created_at)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#A8A29E] mb-1">Last updated</div>
                      <div className="text-sm text-[#57534E]" data-testid="form-drawer-updated">{formatDate(f.updated_at)}</div>
                    </div>
                    {f.slug && (
                      <div className="col-span-2">
                        <div className="text-[10px] uppercase tracking-widest text-[#A8A29E] mb-1">Public URL slug</div>
                        <div className="text-sm text-[#57534E] font-mono break-all">/f/{f.slug}</div>
                      </div>
                    )}
                  </div>

                  {(f.fields || []).length > 0 && (
                    <div className="pt-3 border-t border-[#E8E2D9]">
                      <div className="text-[10px] uppercase tracking-widest text-[#A8A29E] mb-2">Fields ({(f.fields || []).length})</div>
                      <ol className="space-y-1.5" data-testid="form-drawer-fields">
                        {f.fields.map((fld, i) => (
                          <li key={fld.id || i} className="flex items-center gap-2 text-sm">
                            <span className="text-[10px] text-[#A8A29E] w-5 shrink-0">{i + 1}.</span>
                            <span className="flex-1 truncate">{fld.label || <span className="text-[#A8A29E] italic">Untitled</span>}</span>
                            <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-[#FAF8F5] border border-[#E8E2D9] text-[#57534E]">{fld.type}</span>
                            {fld.required && <span className="text-[10px] uppercase tracking-widest text-[#B85C1F]">Required</span>}
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}

                  <div className="pt-4 border-t border-[#E8E2D9] flex items-center gap-2 flex-wrap">
                    {!isReadOnly && (
                      <Button data-testid="form-drawer-edit-btn" onClick={() => openEditFromDrawer(f)} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">
                        <Edit size={14} className="mr-1.5" /> Edit template
                      </Button>
                    )}
                    <Button
                      data-testid="form-drawer-pdf-btn"
                      onClick={async () => {
                        try {
                          const { data: { session } } = await _sb.auth.getSession();
                          if (!session) return toast.error("Please sign in again");
                          await fetchAndSave(
                            `${process.env.REACT_APP_BACKEND_URL}/api/forms/${f.id}/pdf`,
                            { token: session.access_token, filename: `${(f.slug || f.name || "form").replace(/\s+/g, "-").toLowerCase()}.pdf` }
                          );
                        } catch (e) { toast.error(e.message || "PDF download failed"); }
                      }}
                      variant="outline" className="rounded-full"
                    >
                      <Download size={13} className="mr-1.5" /> Download PDF
                    </Button>
                    <Button
                      data-testid="form-drawer-responses-btn"
                      onClick={() => { navigate(`/app/forms/${f.id}/responses`); setDetailForm(null); }}
                      variant="outline" className="rounded-full"
                    >
                      <Layers size={13} className="mr-1.5" /> View responses
                    </Button>
                    {canShare && (
                      <Button data-testid="form-drawer-share-btn" onClick={() => openShare(f)} variant="outline" className="rounded-full">
                        <Link2 size={13} className="mr-1.5" /> Share link
                      </Button>
                    )}
                    {!isReadOnly && (
                      <Button data-testid="form-drawer-duplicate-btn" onClick={() => duplicate(f)} variant="outline" className="rounded-full">
                        <Copy size={13} className="mr-1.5" /> Duplicate
                      </Button>
                    )}
                    {isAdmin && (
                      <Button data-testid="form-drawer-delete-btn" onClick={() => remove(f)} variant="outline" className="rounded-full text-red-600 border-red-200">
                        <Trash2 size={13} className="mr-1.5" /> Delete
                      </Button>
                    )}
                    <Button data-testid="form-drawer-close-btn" onClick={() => setDetailForm(null)} variant="ghost" className="rounded-full ml-auto">
                      <XIcon size={13} className="mr-1" /> Close
                    </Button>
                  </div>

                  {responseCount > 0 && isAdmin && (
                    <div className="pt-3 border-t border-[#E8E2D9]">
                      <div className="text-[10px] text-[#A8A29E] italic">
                        Deleting this template will also delete its <b>{responseCount}</b> submitted response{responseCount === 1 ? "" : "s"} —
                        that is an audit-trail loss. Consider setting the template to <b>Archived</b> instead to hide it
                        from staff pickers while preserving the responses.
                      </div>
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </SheetContent>
      </Sheet>

      <FormBuilderDialog open={builderOpen} onClose={() => setBuilderOpen(false)} editing={editing} orgId={currentOrgId} onSaved={(updated) => { setBuilderOpen(false); load(); if (updated && detailForm && detailForm.id === updated.id) setDetailForm(updated); }} onDelete={remove} isAdmin={isAdmin} />
      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} form={sharing} />
    </div>
  );
}

function FormBuilderDialog({ open, onClose, editing, orgId, onSaved, onDelete, isAdmin }) {
  const [form, setForm] = useState({
    name: "", description: "", fields: [], is_intake: false, is_public: false, status: "draft",
    submit_button_label: "Submit", success_message: "Thank you — your response has been received.", slug: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        name: editing.name, description: editing.description || "",
        fields: editing.fields || [], is_intake: !!editing.is_intake, is_public: !!editing.is_public,
        status: editing.status, submit_button_label: editing.submit_button_label || "Submit",
        success_message: editing.success_message || "", slug: editing.slug || "",
      });
    } else {
      setForm({
        name: "", description: "", fields: [], is_intake: false, is_public: false, status: "draft",
        submit_button_label: "Submit", success_message: "Thank you — your response has been received.", slug: "",
      });
    }
  }, [open, editing]);

  const addField = () => {
    setForm({ ...form, fields: [...form.fields, {
      id: crypto.randomUUID(), type: "text", label: `Question ${form.fields.length + 1}`,
      required: false, options: [], placeholder: "", map_to: "",
    }] });
  };

  const updateField = (i, patch) => {
    const next = [...form.fields];
    next[i] = { ...next[i], ...patch };
    setForm({ ...form, fields: next });
  };

  const removeField = (i) => {
    const next = [...form.fields]; next.splice(i, 1);
    setForm({ ...form, fields: next });
  };

  const moveField = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= form.fields.length) return;
    const next = [...form.fields];
    [next[i], next[j]] = [next[j], next[i]];
    setForm({ ...form, fields: next });
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Give the form a name");
    if (form.fields.length === 0) return toast.error("Add at least one field");
    setSaving(true);
    try {
      const payload = { ...form };
      let updated = null;
      if (editing) {
        updated = await apiFetch(`/forms/${editing.id}`, { method: "PATCH", body: JSON.stringify(payload) });
        // Silent-RLS guard — API returns {} if update was blocked at RLS.
        if (!updated || !updated.id) {
          toast.error("Update was blocked. You may not have permission to change this template.");
          return;
        }
        toast.success("Form saved");
      } else {
        updated = await apiFetch("/forms", { method: "POST", body: JSON.stringify({ ...payload, organization_id: orgId }) });
        toast.success("Form created");
      }
      onSaved(updated);
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">{editing ? "Edit template" : "New template"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-2">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Form name *</Label>
              <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="mt-1.5" data-testid="form-name" />
            </div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                <SelectTrigger className="mt-1.5" data-testid="form-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Description</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="mt-1.5" data-testid="form-description" />
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.is_intake} onChange={(e) => setForm({...form, is_intake: e.target.checked})} className="w-4 h-4 accent-[#E8712C]" data-testid="form-is-intake" />
              Use as intake form
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.is_public} onChange={(e) => setForm({...form, is_public: e.target.checked})} className="w-4 h-4 accent-[#E8712C]" data-testid="form-is-public" />
              Publicly shareable via link
            </label>
          </div>

          <div className="border-t border-[#E8E2D9] pt-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-sm font-semibold">Fields</div>
                <div className="text-xs text-[#57534E]">Drag order · toggle required · map fields onto client record</div>
              </div>
              <Button data-testid="add-field-btn" size="sm" onClick={addField} variant="outline" className="rounded-full"><Plus size={12} className="mr-1" /> Add field</Button>
            </div>
            {form.fields.length === 0 ? (
              <div className="text-center py-6 text-xs text-[#57534E] bg-[#FAF8F5] rounded-2xl border border-dashed border-[#E8E2D9]">
                No fields yet. Add your first field.
              </div>
            ) : (
              <div className="space-y-2">
                {form.fields.map((f, i) => (
                  <div key={f.id || i} className="bg-[#FAF8F5] rounded-2xl border border-[#E8E2D9] p-3 space-y-2" data-testid={`field-row-${i}`}>
                    <div className="flex items-center gap-2">
                      <button onClick={() => moveField(i, -1)} className="text-[#A8A29E] hover:text-[#1C1917] cursor-pointer" title="Move up"><GripVertical size={14} /></button>
                      <Input value={f.label} onChange={(e) => updateField(i, { label: e.target.value })} placeholder="Question label" className="flex-1" data-testid={`field-label-${i}`} />
                      <Select value={f.type} onValueChange={(v) => updateField(i, { type: v })}>
                        <SelectTrigger className="w-36" data-testid={`field-type-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{FIELD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button size="sm" variant="ghost" onClick={() => removeField(i)} className="text-red-600" data-testid={`remove-field-${i}`}><XIcon size={13} /></Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <Input value={f.placeholder || ""} onChange={(e) => updateField(i, { placeholder: e.target.value })} placeholder="Placeholder / help text" className="text-sm" />
                      <Select value={f.map_to || "_none"} onValueChange={(v) => updateField(i, { map_to: v === "_none" ? "" : v })}>
                        <SelectTrigger className="text-sm" data-testid={`field-mapto-${i}`}><SelectValue placeholder="Auto-populate to client record" /></SelectTrigger>
                        <SelectContent>{CLIENT_MAP_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    {(f.type === "select" || f.type === "radio") && (
                      <div>
                        <Label className="text-xs">Options (one per line)</Label>
                        <Textarea rows={3} value={(f.options || []).join("\n")}
                          onChange={(e) => updateField(i, { options: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                          className="mt-1 text-sm" data-testid={`field-options-${i}`} />
                      </div>
                    )}
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                      <input type="checkbox" checked={!!f.required} onChange={(e) => updateField(i, { required: e.target.checked })} className="w-3.5 h-3.5 accent-[#E8712C]" data-testid={`field-required-${i}`} />
                      Required
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 border-t border-[#E8E2D9] pt-4">
            <div><Label>Submit button label</Label>
              <Input value={form.submit_button_label} onChange={(e) => setForm({...form, submit_button_label: e.target.value})} className="mt-1.5" />
            </div>
            <div><Label>Success message</Label>
              <Input value={form.success_message} onChange={(e) => setForm({...form, success_message: e.target.value})} className="mt-1.5" />
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          {editing && isAdmin && (
            <Button data-testid="dialog-delete-form-btn" variant="outline"
                    onClick={() => onDelete(editing)}
                    className="rounded-full text-red-600 border-red-200 mr-auto"><Trash2 size={13} className="mr-1" /> Delete</Button>
          )}
          <Button onClick={onClose} variant="outline" className="rounded-full">Cancel</Button>
          <Button data-testid="save-form-btn" onClick={save} disabled={saving} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">
            {saving ? "Saving…" : editing ? "Save changes" : "Create form"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ShareDialog({ open, onClose, form }) {
  if (!form) return null;
  const url = `${window.location.origin}/f/${form.slug}`;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-lg">
        <DialogHeader><DialogTitle className="font-display tracking-tight">Share this form</DialogTitle></DialogHeader>
        <p className="text-sm text-[#57534E]">Anyone with this link can view and submit responses. Deactivate the form (set to draft/archived) to disable it.</p>
        <div className="flex gap-2">
          <input readOnly value={url} className="flex-1 text-xs px-3 py-2 border border-[#E8E2D9] rounded-lg bg-[#FAF8F5] font-mono" data-testid="share-url" />
          <Button variant="outline" className="rounded-full" onClick={async () => {
            try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch { toast.error("Could not copy"); }
          }}><Copy size={12} className="mr-1" /> Copy</Button>
        </div>
        <a href={url} target="_blank" rel="noreferrer" className="text-xs text-[#E8712C] hover:underline inline-flex items-center gap-1">
          Open in new tab <ExternalLink size={11} />
        </a>
        <DialogFooter><Button onClick={onClose} variant="outline" className="rounded-full">Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
